import { Component } from '@theme/component';
import { prefersReducedMotion, requestIdleCallback } from '@theme/utilities';

/**
 * Hard-cut image loop for Bloks hero media slots.
 * Prioritizes the first image, preloads the rest after idle, and only
 * advances once the next slide is decoded.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class BloksHeroImageLoop extends Component {
  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {number} */
  #current = 0;

  /** @type {boolean} */
  #advancing = false;

  /** @type {boolean} */
  #ready = false;

  connectedCallback() {
    super.connectedCallback();

    const slides = this.refs.slides;
    if (!slides || slides.length < 2) return;

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#boot();
  }

  disconnectedCallback() {
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    super.disconnectedCallback();
  }

  async #boot() {
    const slides = this.refs.slides;
    if (!slides?.length) return;

    await this.#ensureDecoded(slides[0]);
    this.#ready = true;
    this.#preloadRest();

    if (!prefersReducedMotion()) {
      this.play();
    }
  }

  /**
   * Promote remaining slides off the critical path, then decode them.
   */
  #preloadRest() {
    const rest = (this.refs.slides ?? []).slice(1);
    if (!rest.length) return;

    requestIdleCallback(
      () => {
        for (const slide of rest) {
          if (!(slide instanceof HTMLImageElement)) continue;
          if (slide.loading === 'lazy') {
            slide.loading = 'eager';
          }
          this.#ensureDecoded(slide);
        }
      },
      { timeout: 2500 }
    );
  }

  /**
   * @param {HTMLElement | undefined} slide
   */
  async #ensureDecoded(slide) {
    if (!(slide instanceof HTMLImageElement)) return;

    if (!slide.complete || slide.naturalWidth === 0) {
      await new Promise((resolve) => {
        const done = () => resolve(undefined);
        slide.addEventListener('load', done, { once: true });
        slide.addEventListener('error', done, { once: true });
      });
    }

    if (typeof slide.decode === 'function') {
      try {
        await slide.decode();
      } catch {
        // Decode can reject for broken images; still allow advance.
      }
    }
  }

  /**
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (!interval || !this.#ready) return;

    this.suspend();
    this.#interval = setInterval(() => {
      if (document.hidden || this.#advancing) return;
      this.#advance();
    }, interval);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  async #advance() {
    const slides = this.refs.slides;
    if (!slides?.length) return;

    this.#advancing = true;
    try {
      const nextIndex = (this.#current + 1) % slides.length;
      const nextSlide = slides[nextIndex];

      if (nextSlide instanceof HTMLImageElement && nextSlide.loading === 'lazy') {
        nextSlide.loading = 'eager';
      }

      await this.#ensureDecoded(nextSlide);
      this.#current = nextIndex;
      this.#sync();
    } finally {
      this.#advancing = false;
    }
  }

  #sync() {
    this.refs.slides?.forEach((slide, index) => {
      slide.toggleAttribute('hidden', index !== this.#current);
      slide.setAttribute('aria-hidden', `${index !== this.#current}`);
    });
  }

  get autoplayInterval() {
    const value = parseFloat(this.getAttribute('autoplay') || '');
    if (Number.isNaN(value) || value <= 0) return undefined;
    return value * 1000;
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
    } else if (!prefersReducedMotion() && this.#ready) {
      this.play();
    }
  };
}

if (!customElements.get('bloks-hero-image-loop')) {
  customElements.define('bloks-hero-image-loop', BloksHeroImageLoop);
}
